import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { onlyUnique } from 'lib/util/array';
import { format as formatString } from 'lib/util/string';

import { getProfile, User, CFU } from 'lib/personalization/profile';

import SdxSelect from 'sdx/form/Select';

import {
	ListenerModule,
	simpleSubscribe,
	SUBSCRIPTION_TYPES,
} from './Tc.Module.EcommerceProductDataHub';

/*
	Tc.Module.RetentionResult.ts was based on Tc.Module.RetentionDisplay.js (which is deprecated)
 */

interface RetentionContractDetailsModel {
	ContractEndDate : string; // Can be used to display the date, when a retention is possible
	ContractType : 1;
	DaysUntilOffer : 0;
	ExistingAboName : string;
	MaximumAllowedDiscount : string;
	Options : string;
	PremiumDiscount : string;
	ReasonCode : string;
	ReasonText : string;
	Status : 'Red' | 'Green'; // Red: contract is not entitled for a retention offer, Green: contract is entitled
	SubscriptionNumber : string;
	SubscriptionNumberMD5Hash : string;
}

export interface IRetentionChangeData {
	existingAboName : string | undefined;
	status : string;
	subscriptionNumberMD5Hash : string | undefined;
}

// The e-commerce backend can take a very long time to respond
const AJAX_TIMEOUT = 300000;
const enum STATE {
	entInSmeOrRes = 'entinsmeorres',
	error = 'error',
	green = 'green',
	loading = 'loading',
	loggedOut = 'loggedOut',
	noRetentionPossible = 'noretentionpossible',
	retentionResult = 'retentionResult',
}

const ERROR = {
	DATA: 'data',
	TIMEOUT: 'timeout',
};
const HASH_SUFFIX = 'retention';

/**
 * Check if a contract's end date is in the future
 */
function isStillRunning(contract : RetentionContractDetailsModel) {
	const today = new Date().getTime();
	const endDate = getTime(contract.ContractEndDate);

	return endDate > today;
}

/**
 * Returns the time in miliseconds
 */
function getTime(timeString : string) {
	return Number(timeString.slice(6, -7));
}

/**
 * Format a phone number. Example: +41791231234 -> 079 123 12 34
 */
function formattedPhoneNumber(phoneNumber : string) {
	return phoneNumber.replace(/^[+]41/, '0').replace(/^(...)(...)(..)(..)$/, '$1 $2 $3 $4');
}

/**
 * Wrapper for localStorage with expiration time support.
 */
const STORAGE = {
	load(key : string) {
		if (!window.localStorage) {
			return false;
		}
		const record = JSON.parse(localStorage.getItem(key) || '{}');
		if (!record || !record.timestamp || new Date().getTime() > record.timestamp) {
			return false;
		}

		return JSON.parse(record.value);
	},
	save(key : string, jsonData : any, expirationHours : number) {
		if (!window.localStorage) {
			return false;
		}
		const expirationMS = expirationHours * 60 * 60 * 1000;
		const record = {
			timestamp: new Date().getTime() + expirationMS,
			value: JSON.stringify(jsonData),
		};
		localStorage.setItem(key, JSON.stringify(record));

		return jsonData;
	},
};

export default class RetentionResult extends Module implements ListenerModule {
	public productDataChannel : any;

	protected sdxSelect! : SdxSelect;

	protected data : any = {};
	protected noCache = false;
	protected hasProductDataHub = false;
	protected hideActionButton = false;
	protected wasLoggedIn : boolean | undefined;
	protected ajaxError = '';

	protected $loaderSpinner! : JQuery;
	protected $dropdown! : JQuery;
	protected $message! : JQuery;
	protected $paymentSwitch! : JQuery;

	protected contracts : RetentionContractDetailsModel[] = [];

	public on(callback : Hook) {
		simpleSubscribe(this, (data : any) => {
			this.log('simpleSubscribe', data.subscriptionType);
			if (data.subscriptionType === SUBSCRIPTION_TYPES.retention) {
				// Prevent dropdown getting cut off by removing "overflow: hidden" after the animation
				this.$ctx.slideDown(200, () => this.$ctx.removeAttr('style'));
			} else {
				this.$ctx.slideUp(100);
			}
		});

		this.$ctx.hide();
		this.init();

		this.handleLoginStatus().catch(e => this.log.error('Error handling login status', e));

		callback();
	}

	protected async handleLoginStatus() {
		const user = (await getProfile()).getUser();
		this.log('handleLoginChange', user);

		if (user) {
			if (user.getSegment() === CFU.CBU) {
				// ENT customers cannot use this feature. Hence, they are asked to go to the ENT shop.
				this.showUiState(STATE.entInSmeOrRes);
			} else {
				this.loadContractsOverview(user);
			}
		} else {
			this.showUiState(STATE.loggedOut);
		}

		if (this.productDataChannel) {
			this.fire('removeHashParameter', HASH_SUFFIX, [this.productDataChannel]);
		}
	}

	protected init() {
		const mod = this;

		mod.data = mod.$ctx.data();
		mod.noCache = mod.debug && window.location.hash.indexOf('nocache') > 0;
		mod.hasProductDataHub = !!$('.modEcommerceProductDataHub').length;

		mod.$loaderSpinner = mod.$ctx.find('.loader-spinner');
		mod.$dropdown = mod.$ctx.find('.select');
		mod.$message = mod.$ctx.find('.retention-message');

		mod.$dropdown.on('change', mod.handleDropdownChange.bind(mod));
	}

	protected handleDropdownChange(event : Event) {
		const phoneNumber = $(event.target as Element).val();
		this.showUiState(STATE.retentionResult, this.getContractByPhoneNumber(String(phoneNumber)));
		this.log('handleDropdownChange', phoneNumber);
	}

	/**
	 * Loads available contracts for a retention upgrade from the server.
	 * Response (Array of Strings containing ALL phone numbers listed under this account:
	 * ['+41791039311', '+41791039315']
	 */
	protected loadContractsOverview(user : User) {
		const mod = this;
		const SCS = (window as any).SCS;

		mod.contracts = STORAGE.load(`contracts${user.getId()}_${$('html').attr('lang')}`);
		if (mod.contracts && !mod.noCache) {
			mod.log('loaded from storage', mod.contracts);
			mod.updateDropdown(mod.getContracts());
			mod.showRetentionResults();

			return;
		}

		mod.showUiState(STATE.loading);
		mod.contracts = [];

		$.ajax({
			timeout: AJAX_TIMEOUT,
			url: SCS.portal.addParamToUrl(mod.data.urlLoadContracts, 'id', user.getId()),
			success(contracts) {
				if ($.isArray(contracts)) {
					if (contracts.length) {
						mod.updateDropdown(contracts);
						mod.loadContractDetails(user, contracts, 0);
					} else {
						mod.showUiState(STATE.noRetentionPossible);
					}
				} else {
					mod.showError();
				}
			},
			error(xhr, _textStatus, errorThrown) {
				mod.showError(errorThrown, xhr);
			},
		});
	}

	protected getContracts() : string[] {
		return this.contracts.map((contract : any) => contract.SubscriptionNumber);
	}

	protected getContractByPhoneNumber(phoneNumber : string) {
		return this.contracts.find((contract : any) => contract.SubscriptionNumber === phoneNumber);
	}

	protected updateDropdown(contracts : string[] = []) {
		if (!this.sdxSelect) {
			const selectElement = this.$ctx[0].querySelector('select');
			if (!selectElement)  {
				return;
			}
			this.sdxSelect = new SdxSelect(selectElement);
		}

		const uniqueContracts = contracts.filter(onlyUnique);

		this.sdxSelect.empty();
		for (const phoneNumber of uniqueContracts) {
			this.sdxSelect.element.appendChild(new Option(formattedPhoneNumber(phoneNumber), phoneNumber));
		}
		this.sdxSelect.reload();
	}

	/**
	 * Load the details of a single contract from
	 * /portalshop/de/res/Retention/GetRetentionContractDetails?subscriptionNumber={{NUMBER}}
	 * Response: RetentionContractDetailsModel
	 *
	 * @param contracts - Array with cell phone numbers registered under the current user
	 */
	protected loadContractDetails(user : User, contracts : string[], index : number) {
		const mod = this;
		const SCS = (window as any).SCS;
		const url = SCS.portal.addParamToUrl(mod.data.urlLoadContractDetails, 'subscriptionNumber', contracts[index]);

		$.ajax({
			error(xhr, _textStatus, errorThrown) {
				if ((index + 1) < (contracts.length)) {
					mod.loadContractDetails(user, contracts, (index + 1));
				} else {
					mod.showError(errorThrown, xhr);
				}
			},
			success(details) {
				const contractDetails = details.RetentionContractDetailsModel as RetentionContractDetailsModel;
				if (contractDetails && contractDetails.Status.indexOf('error') === -1) {
					mod.contracts.push(contractDetails);
				}

				if ((index + 1) < (contracts.length)) {
					mod.loadContractDetails(user, contracts, (index + 1));
				} else {
					if (contractDetails && contractDetails.Status.indexOf('error') > -1) {
						mod.ajaxError = ERROR.TIMEOUT;
					}
					mod.contractsLoadingHandler();
					STORAGE.save(`contracts${user.getId()}_${$('html').attr('lang')}`, mod.contracts, 1);
				}
			},
			timeout: AJAX_TIMEOUT,
			url,
		});
	}

	protected contractsLoadingHandler() {
		const mod = this;

		if (mod.ajaxError) {
			if (mod.ajaxError === ERROR.TIMEOUT) {
				mod.showError(ERROR.TIMEOUT);
			} else {
				mod.showError();
			}
		} else {
			mod.showRetentionResults();
		}
	}

	/**
	 * Show the results of the load contract details call(s).
	 * Loaded via AJAX or from local storage.
	 */
	protected showRetentionResults() {
		const retentionPossible = this.getFirstWithRetentionPossible();
		if (retentionPossible) {
			this.showUiState(STATE.retentionResult, retentionPossible);
			this.fireTrackingCall('retentionPossible');

			return;
		}

		const futureRetentionPossible = this.getContractWithNearestEndDate();
		if (futureRetentionPossible) {
			this.showUiState(STATE.retentionResult, futureRetentionPossible);
			this.fireTrackingCall('futureRetentionPossible');

			return;
		}

		this.showUiState(STATE.noRetentionPossible);
		this.fireTrackingCall('noRetentionPossible');
	}

	protected getFirstWithRetentionPossible() : RetentionContractDetailsModel | undefined {
		for (const contract of this.contracts) {
			if (contract.Status.toLowerCase() === STATE.green) {

				return contract;
			}
		}

		return undefined;
	}

	protected fireTrackingCall(retentionStatus : string) {
		const interaction = new SCS.portal.datalayer.Interaction()
			.withEvent('response', retentionStatus, 'productDetailPage', 'checker');
		interaction.track();
		this.log('fireTrackingCall', interaction);
	}

	/**
	 * Communicate state change to Tc.Module.PaymentSwitch and Tc.Module.PriceBox.
	 */
	protected updateState(status : string, subscriptionNumberMD5Hash? : string, existingAboName? : string) {
		this.log('updateState', status);
		const data : IRetentionChangeData = {
			existingAboName,
			status,
			subscriptionNumberMD5Hash,
		};
		this.fire('retentionChange', data);
	}

	/**
	 * Switch between different UI states.
	 */
	protected showUiState(uiState : string, contract? : RetentionContractDetailsModel) {
		const mod = this;

		mod.$loaderSpinner.addClass('hidden');
		mod.$dropdown.removeClass('hidden');
		mod.showMessage();
		mod.$ctx.removeClass('hidden');

		switch (uiState) {
			case STATE.loggedOut:
				mod.$ctx.addClass('hidden');
				mod.updateState('loggedOut');
				break;
			case STATE.loading:
				mod.$loaderSpinner.removeClass('hidden');
				mod.$dropdown.addClass('hidden');
				mod.showMessage(mod.data.messageLoading);
				mod.scrollToComponent();
				mod.updateState('loading');
				break;
			case STATE.noRetentionPossible:
				mod.$dropdown.addClass('hidden');
				mod.showMessage(contract && contract.ReasonText || mod.data.messageNoRetentionPossible);
				this.updateState('notPossible');
				break;
			case STATE.retentionResult:
				if (!contract) {
					mod.showMessage(mod.data.messageNoRetentionPossible);
					this.updateState('notPossible');
					break;
				}
				this.$dropdown.find('select').val(contract.SubscriptionNumber);
				this.sdxSelect.reload();
				if (contract.Status.toLowerCase() === STATE.green) {
					mod.showMessage(contract.ReasonText || mod.data.messageRetentionPossible);
					mod.updateState('possible', contract.SubscriptionNumberMD5Hash, contract.ExistingAboName);
				} else if (isStillRunning(contract)) {
					const date = new Date(getTime(contract.ContractEndDate));
					const dateString = `${date.getDate()}. ${(date.getMonth() + 1)}. ${date.getFullYear()}`;
					mod.showMessage(formatString(mod.data.messageFutureRetentionPossible, dateString));
					mod.updateState('futurePossible');
				} else {
					mod.showMessage(contract.ReasonText || mod.data.messageNoRetentionPossible);
					mod.updateState('notPossible');
				}
				break;
			case STATE.entInSmeOrRes:
				mod.showMessage(mod.data.messageCbuInResOrSme);
				mod.updateState('notPossible');
				break;
			default:
			case STATE.error:
				mod.showMessage(mod.data.messageError);
				mod.updateState('notPossible');
				break;
		}
		mod.log('showUiState', uiState, contract);
	}

	protected showMessage(message? : string) {
		if (!message || !message.length) {
			this.$message.text('').addClass('hidden');

			return;
		}

		this.$message.text(message).removeClass('hidden');
	}

	protected scrollToComponent() {
		let scrollTop = 0;
		const offset = this.$ctx.offset();
		if (offset && offset.top) {
			scrollTop = offset.top - 100;
			$('html, body').animate({scrollTop}, 1000);
		}
	}

	/**
	 * Find the contract with the nearest contract end date from today.
	 */
	protected getContractWithNearestEndDate() : RetentionContractDetailsModel | undefined {
		let contract : RetentionContractDetailsModel;
		let nearestContract! : RetentionContractDetailsModel;
		let endDate;
		let nearestEndDate;

		for (let i = 0, length = this.contracts.length; i < length; i += 1) {
			contract = this.contracts[i];
			endDate = getTime(contract.ContractEndDate);

			if ((!nearestEndDate || endDate < nearestEndDate) && isStillRunning(contract)) {
				nearestContract = contract;
				nearestEndDate = endDate;
			}
		}

		return nearestContract;
	}

	/**
	 * Display an error Message.
	 */
	protected showError(errorThrown? : any, xhr? : any) {
		const SCS = (window as any).SCS;
		this.log(errorThrown, xhr);
		if (xhr && xhr.status === SCS.ecommerce.ECOMMERCE_SERVICE_UNAVAILABLE_EXCEPTION) {
			SCS.ecommerce.showEcommerceErrorMessage();
		} else {
			this.showUiState(STATE.error);
		}
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.RetentionResult.ts